import { useState, useRef, useEffect, useMemo } from 'react';
import useInterval from '@use-it/interval';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { isIE11, randomBetween, supportsHEVCAlpha } from '../../utils/utils';
import Backdrop from './Backdrop';
import CaptureOverlay from './CaptureOverlay';
import Template from '../../fiesta/components/Template';

/** @typedef {import('../../types/proposal').FiestaOverlay} FiestaOverlay */
/** @typedef {import('../../types/proposal').ClassicOverlay} ClassicOverlay */
/** @typedef {import('../../types/proposal').ScaledDimensions} ScaledDimensions */

/**
 * @typedef {Object} VideoDemoProps
 * @property {string} [video] - Video source URL for the capture preview
 * @property {string} [image] - Static image URL (e.g., systemAssetId PNG/JPG)
 * @property {ClassicOverlay} [overlay] - Classic (non-fiesta) overlay with animated frames
 * @property {string} [backdrop] - Backdrop image URL
 * @property {boolean} [transparent] - Whether to use transparent video mode (default: false)
 * @property {boolean} [gif] - Whether to use GIF cycling mode (default: false)
 * @property {string[]} [fiestaOverlayIds] - IDs of per-mode fiesta overlays (looked up from Redux)
 * @property {string[]} [greenScreenOverlayIds] - IDs of green screen overlays (looked up from Redux)
 */

/**
 * Renders a capture preview with optional backdrop, overlays, and green screen layers.
 * Supports video, static image, and cycling model image fallback modes.
 * When both green screen and main overlays are present, forces the green screen
 * overlay to match the main overlay's scaled dimensions.
 *
 * @param {VideoDemoProps} props
 */
const VideoDemo = ({
  video,
  image,
  overlay,
  backdrop,
  transparent = false,
  gif = false,
  fiestaOverlayIds,
  greenScreenOverlayIds,
}) => {
  const [videoSrc, setVideoSrc] = useState(null);
  const [model, setModel] = useState(0);

  const fiestaOverlays = useSelector((state) => {
    return _.compact((fiestaOverlayIds ?? []).map((overlayId) => state.proposalOverlays.data[overlayId]));
  });

  const greenScreenOverlays = useSelector((state) => {
    return _.compact((greenScreenOverlayIds ?? []).map((overlayId) => state.proposalOverlays.data[overlayId]));
  });

  const hasFiestaOverlays = fiestaOverlays?.length > 0;
  const hasGreenScreen = greenScreenOverlays?.length > 0;

  // Custom aspect ratio based on overlays
  const ratio = useMemo(() => {
    // Prioritize the Fiesta overlays.
    if (hasFiestaOverlays) {
      return fiestaOverlays[0].height / fiestaOverlays[0].width;
    }

    // If there are no per-mode overlays, prioritize the classic overlay.
    if (overlay) {
      return overlay.dimensions.h / overlay.dimensions.w;
    }

    // Otherwise, use GS overlay.
    if (hasGreenScreen) {
      return greenScreenOverlays[0].height / greenScreenOverlays[0].width;
    }

    return null;
  }, [overlay, fiestaOverlays, hasFiestaOverlays, hasGreenScreen, greenScreenOverlays]);

  const layoutRef = useRef();

  const [renderWidth, setRenderWidth] = useState(0);
  const [renderHeight, setRenderHeight] = useState(0);

  useEffect(() => {
    const handleResize = (entries) => {
      const entry = entries[0];
      setRenderWidth(entry.contentRect.width);
      setRenderHeight(entry.contentRect.height);
    };

    const observer = new ResizeObserver(handleResize);
    const element = layoutRef.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      observer.disconnect();
    };
  }, [videoSrc]);

  useEffect(() => {
    // Set transparent video src if necessary
    if (transparent && !isIE11()) {
      if (supportsHEVCAlpha()) {
        setVideoSrc('/static/model/video/0.mov');
      } else {
        setVideoSrc('/static/model/video/0.webm');
      }
    } else if (video) {
      setVideoSrc(video);
    }
    // Set random model (for fallback when no video/image provided)
    setModel(randomBetween(4, 6));
  }, [video]);

  // Compute main overlay scaled dimensions so the green screen overlay can be forced
  // to the same size, matching the guest app's behavior where the GS overlay stretches
  // to fill the final capture frame defined by the main overlay.
  const hasMainOverlay = hasFiestaOverlays || !!overlay;

  /**
   * The main overlay's dimensions after scaling to fit within the render container.
   * Used to force the green screen overlay to the same size when both are present.
   * @type {ScaledDimensions | null}
   */
  const mainOverlayScaled = useMemo(() => {
    if (!hasMainOverlay || !hasGreenScreen || !renderWidth || !renderHeight) {
      return null;
    }

    let mainWidth;
    let mainHeight;
    if (hasFiestaOverlays) {
      mainWidth = fiestaOverlays[0].width;
      mainHeight = fiestaOverlays[0].height;
    } else if (overlay?.dimensions?.w && overlay?.dimensions?.h) {
      mainWidth = overlay.dimensions.w;
      mainHeight = overlay.dimensions.h;
    } else {
      return null;
    }

    if (!mainWidth || !mainHeight) {
      return null;
    }

    const scaleFactor = Math.min(renderWidth / mainWidth, renderHeight / mainHeight);
    return {
      width: mainWidth * scaleFactor,
      height: mainHeight * scaleFactor,
    };
  }, [hasMainOverlay, hasGreenScreen, hasFiestaOverlays, fiestaOverlays, overlay, renderWidth, renderHeight]);

  // "GIF" mode if video is unavailable
  const totalFrames = 4;
  const [currentFrame, setCurrentFrame] = useState(0);
  useInterval(
    () => {
      setCurrentFrame((f) => (f === totalFrames - 1 ? 0 : f + 1));
    },
    videoSrc || !gif ? null : 1000
  );

  return (
    <div
      className="video-demo"
      style={{
        paddingBottom: ratio ? `calc(60% * ${ratio})` : undefined,
      }}
    >
      <Backdrop ref={backdrop ? layoutRef : null} backdrop={backdrop} animated={video || transparent} />

      {image ? (
        // Render static image (e.g., systemAssetId)
        <img
          ref={backdrop ? null : layoutRef}
          className="video-demo__photo"
          src={image}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      ) : videoSrc ? (
        // Render video
        <video
          ref={backdrop ? null : layoutRef}
          className="video-demo__video"
          src={videoSrc}
          autoPlay
          loop
          playsInline
          muted
        />
      ) : (
        // Fallback: Render cycling model images (GIF mode simulation).
        // Use object-fit: contain so the model is always contained within and centered
        // in the preview regardless of the container's aspect ratio, instead of sizing
        // by raw render dimensions which let a portrait model overflow the bounding box
        // (e.g. with a square overlay for GIFs — PB-7535).
        <img
          className="video-demo__photo"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          src={`/static/model/${model}/${currentFrame}.png`}
          alt=""
        />
      )}

      {hasGreenScreen && (
        <Template
          key={greenScreenOverlays[0].id}
          templateId={greenScreenOverlays[0]?.id}
          thumbnailUrl={greenScreenOverlays[0]?.imageUrl}
          placeholders={greenScreenOverlays[0].placeholders}
          images={greenScreenOverlays[0].images}
          texts={greenScreenOverlays[0].texts}
          width={greenScreenOverlays[0].width}
          height={greenScreenOverlays[0].height}
          renderWidth={renderWidth}
          renderHeight={renderHeight}
          transparentBackground={true}
          style={{ position: 'absolute' }}
          // When a main overlay exists, size the green screen overlay to the main
          // overlay's scaled frame, but keep the GS overlay's own aspect ratio and
          // center it within that frame ('contain') rather than stretching it to fill.
          // Stretching distorted a portrait GS overlay to the (e.g. landscape) capture
          // aspect ratio; the GS overlay must stay centered regardless of the capture's
          // aspect ratio, matching the other capture modes and guest mode (PB-7535).
          {...(mainOverlayScaled
            ? {
                forceRenderWidth: mainOverlayScaled.width,
                forceRenderHeight: mainOverlayScaled.height,
                thumbnailResizeMode: 'contain',
              }
            : {})}
        />
      )}

      {hasFiestaOverlays && (
        <Template
          key={fiestaOverlays[0].id}
          templateId={fiestaOverlays[0]?.id}
          thumbnailUrl={fiestaOverlays[0]?.imageUrl}
          placeholders={fiestaOverlays[0].placeholders}
          images={fiestaOverlays[0].images}
          texts={fiestaOverlays[0].texts}
          width={fiestaOverlays[0].width}
          height={fiestaOverlays[0].height}
          renderWidth={renderWidth}
          renderHeight={renderHeight}
          transparentBackground={true}
          style={{ position: 'absolute' }}
        />
      )}

      {!hasFiestaOverlays && overlay && <CaptureOverlay overlay={overlay} animate={!!(videoSrc || gif)} />}
    </div>
  );
};

export default VideoDemo;
